import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, where } from "firebase/firestore";
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import '../styles/CostTracking.css';
import Loading from './Loading';

function CostTracking() {
  const [projects, setProjects] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [calculatedValues, setCalculatedValues] = useState({}); // { projectId: { profit, projectValue, ... } }
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch subcontractors
        const subSnapshot = await getDocs(collection(db, "subcontractors"));
        const subcontractorsList = [];
        subSnapshot.forEach((doc) => {
          subcontractorsList.push({ id: doc.id, ...doc.data() });
        });
        setSubcontractors(subcontractorsList);

        // Fetch projects
        const querySnapshot = await getDocs(collection(db, "projects"));
        const projectsList = [];
        querySnapshot.forEach((doc) => {
          projectsList.push({ id: doc.id, ...doc.data() });
        });
        setProjects(projectsList);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate values for all projects when data is loaded
  useEffect(() => {
    if (projects.length === 0 || subcontractors.length === 0) return;

    const calculateAllValues = async () => {
      const values = {};
      for (const project of projects) {
        try {
          // Fetch timesheet entries for this project
          const qTimes = query(
            collection(db, 'timesheets'),
            where('projectId', '==', project.id)
          );
          const snap = await getDocs(qTimes);
          const countBySub = new Map();
          snap.forEach((d) => {
            const data = d.data();
            const subId = data.subcontractorId;
            if (!subId) return;
            countBySub.set(subId, (countBySub.get(subId) || 0) + 1);
          });

          // Calculate labour costs from timesheets
          let totalLabourCost = 0;
          countBySub.forEach((days, subId) => {
            const sub = subcontractors.find((s) => s.id === subId);
            const dayRate = sub?.dayRate || 0;
            totalLabourCost += Number(days) * Number(dayRate);
          });
          totalLabourCost = Number(totalLabourCost.toFixed(2));

          // Get invoices from project data
          const invoices = project.invoices || [];
          const totalInvoices = Number(invoices.reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0).toFixed(2));
          
          // Calculate totals
          const totalCosts = Number((totalLabourCost + totalInvoices).toFixed(2));
          const projectValue = Number(project.projectValue || 0);
          const profit = Number((projectValue - totalCosts).toFixed(2));

          values[project.id] = {
            projectValue,
            totalLabourCost,
            totalInvoices,
            totalCosts,
            profit
          };
        } catch (e) {
          console.error('Error calculating project values:', e);
          // Fallback to stored values
          values[project.id] = {
            projectValue: Number(project.projectValue || 0),
            totalLabourCost: Number(project.totalLabourCost || 0),
            totalInvoices: Number(project.totalInvoices || 0),
            totalCosts: Number(project.totalCosts || 0),
            profit: Number(project.profit || 0)
          };
        }
      }
      setCalculatedValues(values);
    };

    calculateAllValues();
  }, [projects, subcontractors]);

  const deleteProject = async (projectId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, "projects", projectId));
      setProjects(projects.filter(project => project.id !== projectId));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting project:", error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentProjects = projects.filter(project => !project.completionDate);
  const completedProjects = projects.filter(project => project.completionDate)
    .sort((a, b) => new Date(b.completionDate) - new Date(a.completionDate));

  const handleProjectClick = (projectId, e) => {
    if (e.target.closest('.delete-button')) {
      return;
    }
    
    console.log('Navigating to:', `/edit-project/${projectId}`);
    
    navigate(`/edit-project/${projectId}`);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="projects-container">
      <div className="projects-header">
        <div className="projects-header-left">
          <Link to="/dashboard" className="home-button">
            ← Home
          </Link>
          <h1 className="projects-title">Cost Tracking</h1>
        </div>
        <Link to="/create-project" className="create-project-button">
          Create New Project
        </Link>
      </div>

      {/* Current Projects Section */}
      <div className="project-section">
        <h2 style={{ color: '#2AA4F7', marginTop: 0 }}>Current Projects</h2>
        <div className="project-grid">
          {currentProjects.map(project => {
            const calc = calculatedValues[project.id] || {};
            const profit = calc.profit !== undefined ? calc.profit : (project.profit || 0);
            const projectValue = calc.projectValue !== undefined ? calc.projectValue : (project.projectValue || 0);
            
            return (
              <div 
                key={project.id}
                onClick={(e) => handleProjectClick(project.id, e)}
                className="project-card"
              >
                <div className="project-info">
                  <div className="project-name">{project.name}</div>
                  <div className="project-status">Status: In Progress</div>
                </div>
                <div className="project-numbers">
                  <div className="project-values">
                    <div className="project-value">£{projectValue.toFixed(2)}</div>
                    <div className="project-profit" style={{ color: profit >= 0 ? '#2AA4F7' : '#6635FF' }}>
                      £{profit.toFixed(2)}
                    </div>
                    <div className="project-margin" style={{ color: profit >= 0 ? '#2AA4F7' : '#6635FF' }}>
                      Margin: {projectValue ? ((profit / projectValue) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <button
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(project.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
          {currentProjects.length === 0 && (
            <p className="no-projects">No current projects.</p>
          )}
        </div>
      </div>

      {/* Completed Projects Section */}
      <div style={{ 
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ color: '#2c3e50', marginTop: 0 }}>Completed Projects</h2>
        <div style={{ display: 'grid', gap: '15px' }}>
          {completedProjects.map(project => {
            const calc = calculatedValues[project.id] || {};
            const profit = calc.profit !== undefined ? calc.profit : (project.profit || 0);
            const projectValue = calc.projectValue !== undefined ? calc.projectValue : (project.projectValue || 0);
            
            return (
              <div 
                key={project.id}
                onClick={(e) => handleProjectClick(project.id, e)}
                style={{
                  padding: '15px',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ 
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#4CAF50',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px'
                  }}>
                    ✓
                  </div>
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{project.name}</div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>
                      Completed: {new Date(project.completionDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ marginBottom: '5px' }}>£{projectValue.toFixed(2)}</div>
                    <div style={{ 
                      color: profit >= 0 ? '#2AA4F7' : '#6635FF',
                      marginBottom: '5px'
                    }}>
                      £{profit.toFixed(2)}
                    </div>
                    <div style={{ 
                      fontSize: '0.9em',
                      color: profit >= 0 ? '#2AA4F7' : '#6635FF',
                      fontWeight: 'bold'
                    }}>
                      Margin: {projectValue ? ((profit / projectValue) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <button
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(project.id);
                    }}
                    style={{
                      padding: '8px',
                      backgroundColor: '#ffecec',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#dc3545'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
          {completedProjects.length === 0 && (
            <p style={{ textAlign: 'center', color: '#666' }}>No completed projects.</p>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-modal">
          <div className="modal-content">
            <h2>Delete Project?</h2>
            <p>Are you sure you want to delete this project? This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteProject(showDeleteConfirm)}
                className="delete-confirm-button"
              >
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CostTracking;
